import { useEffect, useRef, useState } from 'react'
import { ImagePlus, X } from 'lucide-react'
import FilePreviewModal from '../ui/FilePreviewModal'
import { STEP_IMAGE_ACCEPT, uploadStepImage, validateStepImage } from '../../lib/stepImages'
import './StepImages.css'

function DraftImage({ file }) {
    const image = useRef(null)
    useEffect(() => {
        const url = URL.createObjectURL(file)
        image.current.src = url
        return () => URL.revokeObjectURL(url)
    }, [file])
    return <img ref={image} className="step-image-draft" alt={file.name} />
}

export default function StepImages({ images = [], procedureId, onChange, draft = false }) {
    const input = useRef(null)
    const busyRef = useRef(false)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')
    const [preview, setPreview] = useState(null)
    const files = Array.isArray(images) ? images : []
    const save = async (next) => {
        const result = await onChange(next)
        if (result?.error) throw new Error(result.error.message || String(result.error))
    }
    const add = async (selected) => {
        if (busyRef.current || !selected.length) return
        busyRef.current = true
        setBusy(true)
        setError('')
        try {
            for (const file of selected) {
                const problem = validateStepImage(file)
                if (problem) throw new Error(problem)
            }
            const added = []
            for (const file of selected) {
                if (draft) added.push({ id: crypto.randomUUID(), name: file.name, file })
                else added.push(await uploadStepImage(procedureId, file))
            }
            await save([...files, ...added])
        } catch (e) { setError(e.message) }
        finally { busyRef.current = false; setBusy(false) }
    }
    const remove = async (id) => {
        if (busyRef.current) return
        busyRef.current = true
        setBusy(true)
        setError('')
        try { await save(files.filter((file) => file.id !== id)) }
        catch (e) { setError(e.message) }
        finally { busyRef.current = false; setBusy(false) }
        // Stored files are retained: completed task snapshots may still use them.
    }
    if (!onChange && !files.length) return null
    return (
        <div className="step-images" onPaste={onChange ? (event) => {
            const pasted = Array.from(event.clipboardData?.files || [])
            if (!pasted.length) return
            event.preventDefault()
            event.stopPropagation()
            add(pasted)
        } : undefined}>
            {files.map((file) => (
                <div className="step-image" key={file.id}>
                    {file.url ? <button type="button" className="step-image-preview" onClick={() => setPreview(file)} aria-label={`Deschide ${file.name}`}>
                        <img src={file.url} alt={file.name} loading="lazy" />
                    </button> : <DraftImage file={file.file} />}
                    {onChange ? <button type="button" className="step-image-remove" disabled={busy} onClick={() => remove(file.id)} aria-label={`Elimină ${file.name}`}><X size={13} /></button> : null}
                </div>
            ))}
            {onChange ? <>
                <input ref={input} type="file" accept={STEP_IMAGE_ACCEPT} multiple hidden onChange={(event) => { add(Array.from(event.target.files || [])); event.target.value = '' }} />
                <button type="button" className="step-image-add" disabled={busy} onClick={() => input.current?.click()} aria-label="Adaugă poză la pas"><ImagePlus size={15} />{busy ? 'Se încarcă…' : 'Poză'}</button>
            </> : null}
            {error ? <span className="step-image-error" role="alert">{error}</span> : null}
            {preview ? <FilePreviewModal file={preview} onClose={() => setPreview(null)} /> : null}
        </div>
    )
}
